import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AssignmentIcon from "@material-ui/icons/Assignment";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import PersonIcon from "@material-ui/icons/Person";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import SecurityIcon from "@material-ui/icons/Security";
import TimerIcon from "@material-ui/icons/Timer";
import { useAppSelector } from "app/hooks";
import { selectInfoLogin } from "features/Auth/authSlice";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import managerTestApi from "api/managerTest";

export interface TakeATestProps {}

export default function TakeATest(props: TakeATestProps) {
  const useStyles = makeStyles((theme: Theme) => ({
    container: {
      paddingTop: 32,
      paddingBottom: 48,
      [theme.breakpoints?.down("sm")]: {
        paddingTop: 16,
        paddingBottom: 24,
      },
    },
    backButton: {
      marginBottom: 20,
      color: "#475569",
      fontWeight: 700,
      fontSize: "0.875rem",
      textTransform: "none",
      borderRadius: 10,
      padding: "6px 14px",
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#F1F5F9",
        color: "#0F172A",
        transform: "translateX(-2px)",
      },
    },
    headerBanner: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #312E81 100%)",
      borderRadius: 20,
      padding: "32px 36px",
      color: "#FFFFFF",
      marginBottom: 28,
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.25)",
      [theme.breakpoints?.down("sm")]: {
        padding: "20px 20px",
        borderRadius: 16,
      },
    },
    headerGlow: {
      position: "absolute",
      top: "-40%",
      right: "-10%",
      width: 320,
      height: 320,
      background: "radial-gradient(circle, rgba(99, 102, 241, 0.35) 0%, rgba(99, 102, 241, 0) 70%)",
      pointerEvents: "none",
    },
    headerPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      backdropFilter: "blur(8px)",
      padding: "4px 14px",
      borderRadius: 20,
      fontSize: "0.75rem",
      fontWeight: 800,
      color: "#818CF8",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: 12,
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },
    headerTitle: {
      fontSize: "1.75rem",
      fontWeight: 900,
      color: "#FFFFFF",
      marginBottom: 6,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "1.25rem",
      },
    },
    headerSubtitle: {
      fontSize: "0.95rem",
      color: "#94A3B8",
      fontWeight: 500,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "0.825rem",
      },
    },

    // Info Card Layout
    infoCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: 30,
      border: "1px solid #E2E8F0",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.04)",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      [theme.breakpoints?.down("sm")]: {
        padding: 20,
      },
    },
    sectionTitleRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 20,
      paddingBottom: 12,
      borderBottom: "1px dashed #E2E8F0",
    },
    sectionTitleText: {
      fontSize: "1.05rem",
      fontWeight: 800,
      color: "#0F172A",
    },
    infoRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: 14,
      marginBottom: 18,
      "&:last-child": {
        marginBottom: 0,
      },
    },
    infoIconWrapper: {
      width: 42,
      height: 42,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    infoLabel: {
      fontSize: "0.775rem",
      color: "#64748B",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      marginBottom: 2,
    },
    infoValue: {
      fontSize: "1.05rem",
      color: "#0F172A",
      fontWeight: 800,
    },

    // Control Card (Right Panel)
    controlCard: {
      background: "linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)",
      borderRadius: 20,
      padding: 30,
      border: "1px solid #CBD5E1",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      height: "100%",
      minHeight: 340,
      [theme.breakpoints?.down("sm")]: {
        padding: 20,
        minHeight: 280,
      },
    },
    timerCircle: {
      width: 140,
      height: 140,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
      border: "4px solid #C7D2FE",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 24,
      boxShadow: "0 8px 24px rgba(99, 102, 241, 0.15)",
    },
    timerDigits: {
      fontSize: "2.4rem",
      fontWeight: 900,
      color: "#4F46E5",
      fontFamily: "monospace",
      lineHeight: 1,
    },
    timerLabel: {
      fontSize: "0.75rem",
      fontWeight: 800,
      color: "#6366F1",
      textTransform: "uppercase",
      marginTop: 4,
    },
    btnStart: {
      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
      borderRadius: 14,
      color: "#FFFFFF",
      padding: "14px 36px",
      fontSize: "1.1rem",
      fontWeight: 900,
      textTransform: "none",
      boxShadow: "0 8px 24px rgba(16, 185, 129, 0.4)",
      transition: "all 0.25s ease",
      width: "100%",
      maxWidth: 280,
      "&:hover": {
        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
        boxShadow: "0 12px 30px rgba(16, 185, 129, 0.6)",
        transform: "translateY(-2px)",
      },
    },
    ruleNotice: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: "#FEF3C7",
      color: "#92400E",
      borderRadius: 12,
      padding: "10px 16px",
      marginTop: 20,
      fontSize: "0.825rem",
      fontWeight: 700,
      border: "1px solid #FDE68A",
    },
  }));

  const classes = useStyles();
  const { t } = useTranslation();
  const location: any = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down("sm"));
  const navigate = useNavigate();
  const infoUser = useAppSelector(selectInfoLogin);

  const { id: paramId } = useParams();
  const [fetchedExam, setFetchedExam] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!location?.state?.startDate && !!paramId);

  useEffect(() => {
    if (paramId && !location?.state?.startDate) {
      setIsLoading(true);
      managerTestApi
        .getDetailExam(Number(paramId))
        .then((res: any) => {
          if (res?.exam) {
            setFetchedExam({
              id: res.exam.id,
              examTitle: res.exam.title,
              startDate: res.exam.startDate,
              prize: res.exam.prize,
            });
          }
        })
        .catch(() => {})
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [paramId, location?.state]);

  // Fallback state if location.state is missing so page can always be viewed directly
  const examInfo = location?.state || fetchedExam || {
    id: paramId ? Number(paramId) : 468,
    examTitle: paramId
      ? `Bài thi #${paramId}`
      : "🔥 [LÀM BÀI NGAY] Kiểm Tra Nhanh An Toàn Sinh Học GPET 2026",
    startDate: paramId ? null : new Date(),
    prize: { name: "Huy chương Vàng GPET + 500.000 VNĐ" },
  };

  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!examInfo?.startDate) return;

    const updateTimer = () => {
      const currentMs = Date.now();
      const startMs = new Date(examInfo.startDate).getTime();
      const diff = moment.duration(moment(startMs).diff(moment(currentMs)));

      if (diff.asMilliseconds() <= 0) {
        setDays(0);
        setHours(0);
        setMinutes(0);
        setSeconds(0);
        setIsReady(true);
      } else {
        setIsReady(false);
        setDays(Math.floor(diff.asDays()));
        setHours(diff.hours());
        setMinutes(diff.minutes());
        setSeconds(diff.seconds());
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [examInfo?.startDate]);

  const handleStartExam = () => {
    navigate("/exam/take-exam", {
      state: {
        id: examInfo.id,
        startTime: new Date(),
      },
    });
  };

  return (
    <Container maxWidth="lg" className={classes.container}>
      {/* Back to Upcoming List Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        className={classes.backButton}
        onClick={() => navigate("/exam-upcoming")}
      >
        Quay lại
      </Button>

      {/* Header Banner */}
      <Box className={classes.headerBanner}>
        <Box className={classes.headerGlow} />
        <Box className={classes.headerPill}>
          <SecurityIcon style={{ fontSize: 16 }} />
          CỔNG CHỜ THI TRỰC TUYẾN GPET
        </Box>
        <Typography className={classes.headerTitle}>
          {examInfo?.examTitle || "Bài Thi Kiểm Tra Năng Lực Trực Tuyến"}
        </Typography>
        <Typography className={classes.headerSubtitle}>
          Vui lòng kiểm tra kỹ thông tin cá nhân và quy định làm bài trước khi bấm bắt đầu.
        </Typography>
      </Box>

      {/* Main Grid Section */}
      <Grid container spacing={3} alignItems="stretch">
        {/* Left Side: Exam & Student Details */}
        <Grid item xs={12} md={7}>
          <Paper className={classes.infoCard} elevation={0}>
            <Box>
              <Box className={classes.sectionTitleRow}>
                <AssignmentIcon style={{ color: "#4F46E5", fontSize: 24 }} />
                <Typography className={classes.sectionTitleText}>
                  THÔNG TIN CHI TIẾT BÀI THI & THÍ SINH
                </Typography>
              </Box>

              {/* Student Name */}
              <Box className={classes.infoRow}>
                <Box className={classes.infoIconWrapper} style={{ backgroundColor: "#EEF2FF", color: "#4F46E5" }}>
                  <PersonIcon />
                </Box>
                <Box>
                  <Typography className={classes.infoLabel}>{t("auth.fullName")}</Typography>
                  <Typography className={classes.infoValue}>
                    {infoUser?.name || "Hồng"} <span style={{ fontSize: "0.85rem", color: "#64748B", fontWeight: 600 }}>(Mã NV: {infoUser?.staffId || "H201296"})</span>
                  </Typography>
                </Box>
              </Box>

              {/* Exam Title */}
              <Box className={classes.infoRow}>
                <Box className={classes.infoIconWrapper} style={{ backgroundColor: "#F0FDF4", color: "#16A34A" }}>
                  <AssignmentIcon />
                </Box>
                <Box>
                  <Typography className={classes.infoLabel}>{t("listexam.tieudebaithi")}</Typography>
                  <Typography className={classes.infoValue}>{examInfo?.examTitle}</Typography>
                </Box>
              </Box>

              {/* Prize */}
              <Box className={classes.infoRow}>
                <Box className={classes.infoIconWrapper} style={{ backgroundColor: "#FEF3C7", color: "#D97706" }}>
                  <EmojiEventsIcon />
                </Box>
                <Box>
                  <Typography className={classes.infoLabel}>{t("listexam.phanthuong")}</Typography>
                  <Typography className={classes.infoValue}>
                    {examInfo?.prize?.name || "Giấy khen Xuất sắc + Phần thưởng GPET Edu"}
                  </Typography>
                </Box>
              </Box>

              {/* Duration */}
              <Box className={classes.infoRow}>
                <Box className={classes.infoIconWrapper} style={{ backgroundColor: "#EFF6FF", color: "#2563EB" }}>
                  <TimerIcon />
                </Box>
                <Box>
                  <Typography className={classes.infoLabel}>Thời gian làm bài quy định</Typography>
                  <Typography className={classes.infoValue}>30 phút (Tự động nộp bài khi hết giờ)</Typography>
                </Box>
              </Box>
            </Box>

            {/* Security Notice */}
            <Box className={classes.ruleNotice}>
              <CheckCircleOutlineIcon style={{ color: "#D97706" }} />
              <Typography style={{ fontSize: "0.825rem", fontWeight: 700 }}>
                Hệ thống tự động chấm điểm và ghi nhận kết quả ngay sau khi nộp bài.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Right Side: Timer & Action Control */}
        <Grid item xs={12} md={5}>
          <Paper className={classes.controlCard} elevation={0}>
            {isLoading ? (
              <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 0" }}>
                <CircularProgress style={{ color: "#4F46E5", marginBottom: 16 }} size={48} />
                <Typography style={{ fontSize: "0.95rem", fontWeight: 700, color: "#64748B" }}>
                  Đang tải thông tin bài thi #{paramId}...
                </Typography>
              </Box>
            ) : isReady ? (
              <>
                <Box className={classes.timerCircle} style={{ background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)", borderColor: "#A7F3D0" }}>
                  <PlayArrowIcon style={{ fontSize: 56, color: "#059669" }} />
                  <Typography className={classes.timerLabel} style={{ color: "#047857" }}>
                    SẴN SÀNG
                  </Typography>
                </Box>

                <Typography style={{ fontSize: "1.2rem", fontWeight: 900, color: "#0F172A", marginBottom: 8 }}>
                  Cổng bài thi đã mở!
                </Typography>
                <Typography style={{ fontSize: "0.875rem", color: "#64748B", marginBottom: 24, padding: "0 10px" }}>
                  Bấm nút bên dưới để tính thời gian và bắt đầu làm bài.
                </Typography>

                <Button className={classes.btnStart} endIcon={<PlayArrowIcon />} onClick={handleStartExam}>
                  BẮT ĐẦU THI
                </Button>
              </>
            ) : (
              <>
                <Box className={classes.timerCircle}>
                  <TimerIcon style={{ color: "#6366F1", fontSize: 26, marginBottom: 6 }} />
                  <Typography
                    className={classes.timerDigits}
                    style={{ fontSize: days > 0 ? "1.6rem" : "2.2rem" }}
                  >
                    {days > 0
                      ? `${days}d ${hours}h`
                      : hours > 0
                      ? `${hours}h ${minutes}m`
                      : `${minutes < 10 ? "0" + minutes : minutes}:${seconds < 10 ? "0" + seconds : seconds}`}
                  </Typography>
                </Box>

                <Chip
                  label="ĐANG ĐỢI MỞ CỔNG THI"
                  size="small"
                  style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontWeight: 800, marginBottom: 16 }}
                />

                <Typography style={{ fontSize: "0.875rem", color: "#64748B", marginBottom: 8 }}>
                  Nút "Bắt đầu thi" sẽ tự động hiển thị khi đếm ngược kết thúc.
                </Typography>

                <Button
                  disabled
                  style={{
                    backgroundColor: "#F1F5F9",
                    color: "#94A3B8",
                    fontWeight: 800,
                    borderRadius: 14,
                    padding: "12px 28px",
                    marginTop: 12,
                    textTransform: "none",
                    border: "1px solid #E2E8F0",
                    width: "100%",
                    maxWidth: 280,
                  }}
                >
                  CHƯA ĐẾN GIỜ THI
                </Button>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
